import React, { useEffect, useState } from "react";
import { api } from "@/lib/api";
import { useI18n } from "@/context/I18nContext";
import { Plus, X, Trash2, Pencil, History, ListPlus, CheckSquare, Square } from "lucide-react";
import { CountrySelect, PhoneWithCountry } from "@/components/CountrySelect";
import { countryLabel, flagFromCode } from "@/lib/countries";
import CallButton from "@/components/CallButton";
import ContactTimeline from "@/components/ContactTimeline";
import StatusBadge from "@/components/StatusBadge";

export default function Contacts() {
  const { t, lang } = useI18n();
  const L = lang === "el";
  const [list, setList] = useState([]);
  const [open, setOpen] = useState(false);
  const [editing, setEditing] = useState(null);
  const blankForm = {
    first_name: "", last_name: "", company_name: "", position: "", email: "",
    phone: "", country: "", country_code: "", industry: "", website: "", linkedin: "",
    notes: "", in_calling_list: false,
  };
  const [form, setForm] = useState(blankForm);
  const [q, setQ] = useState("");
  const [timelineFor, setTimelineFor] = useState(null);

  const load = async () => {
    const { data } = await api.get(`/contacts${q ? `?search=${encodeURIComponent(q)}` : ""}`);
    setList(data);
  };
  useEffect(() => { load(); /* eslint-disable-next-line */ }, [q]);

  const openCreate = () => { setEditing(null); setForm(blankForm); setOpen(true); };
  const openEdit = (c) => {
    setEditing(c);
    setForm({ ...blankForm, ...c });
    setOpen(true);
  };
  const submit = async (e) => {
    e.preventDefault();
    const payload = { ...form };
    if (editing) await api.patch(`/contacts/${editing.id}`, payload);
    else await api.post("/contacts", payload);
    setOpen(false); load();
  };
  const del = async (id) => {
    if (!window.confirm(L ? "Διαγραφή επαφής;" : "Delete contact?")) return;
    await api.delete(`/contacts/${id}`); load();
  };

  const toggleList = async (c) => {
    await api.patch(`/contacts/${c.id}`, { in_calling_list: !c.in_calling_list });
    load();
  };

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between flex-wrap gap-3">
        <div>
          <div className="text-xs uppercase tracking-[0.2em] font-semibold text-slate-500">{t("nav.customers")}</div>
          <h1 className="font-heading text-3xl font-bold text-slate-900 tracking-tight mt-1">
            {L ? "Επαφές & Εταιρείες" : "Customers"}
          </h1>
          <p className="text-sm text-slate-500 mt-1">
            {L ? "Διαχείριση πελατών — κάθε επαφή είναι μια εταιρεία με σημείο επαφής" : "Manage customers — each contact is a company with a point-of-contact"}
          </p>
        </div>
        <div className="flex items-center gap-2">
          <input
            data-testid="contacts-search"
            type="text"
            value={q}
            onChange={(e) => setQ(e.target.value)}
            placeholder={L ? "Αναζήτηση..." : "Search..."}
            className="px-3 py-2 border border-slate-200 rounded-md text-sm w-56"
          />
          <button data-testid="contact-create" onClick={openCreate} className="inline-flex items-center gap-2 px-4 py-2 bg-[#0052CC] text-white rounded-md text-sm font-semibold">
            <Plus className="w-4 h-4" /> {L ? "Νέα επαφή" : "Add contact"}
          </button>
        </div>
      </div>

      <div className="bg-white border border-slate-200 rounded-lg overflow-hidden" data-testid="contacts-table">
        <table className="w-full text-sm">
          <thead>
            <tr className="border-b border-slate-200 bg-slate-50">
              {["", L ? "Εταιρεία" : "Company", L ? "Επαφή" : "Contact", L ? "Email" : "Email", L ? "Τηλέφωνο" : "Phone", L ? "Χώρα" : "Country", "Status", L ? "Ενέργειες" : "Actions"].map((h, i) => (
                <th key={i} className="py-3 px-3 text-left text-xs uppercase tracking-[0.2em] font-semibold text-slate-500">{h}</th>
              ))}
            </tr>
          </thead>
          <tbody>
            {list.length === 0 ? (
              <tr><td colSpan={8} className="py-12 text-center text-slate-400">{t("common.no_data")}</td></tr>
            ) : list.map((c) => (
              <tr key={c.id} className="border-b border-slate-100 hover:bg-slate-50">
                <td className="py-2.5 px-3 w-8">
                  <button
                    data-testid={`contact-list-toggle-${c.id}`}
                    onClick={() => toggleList(c)}
                    title={c.in_calling_list ? (L ? "Αφαίρεση από λίστα κλήσεων" : "Remove from calling list") : (L ? "Προσθήκη στη λίστα κλήσεων" : "Add to calling list")}
                    className={c.in_calling_list ? "text-emerald-600" : "text-slate-300 hover:text-[#0052CC]"}
                  >
                    {c.in_calling_list ? <CheckSquare className="w-5 h-5" /> : <Square className="w-5 h-5" />}
                  </button>
                </td>
                <td className="py-2.5 px-3 font-semibold text-slate-900">{c.company_name || "—"}</td>
                <td className="py-2.5 px-3 text-slate-700">
                  <div>{c.first_name} {c.last_name}</div>
                  {c.position && <div className="text-xs text-slate-500">{c.position}</div>}
                </td>
                <td className="py-2.5 px-3 text-slate-700 text-xs">{c.email || "—"}</td>
                <td className="py-2.5 px-3 text-slate-700 text-xs">
                  {c.phone ? (
                    <div className="flex items-center gap-1.5">
                      <span>{c.phone}</span>
                      <CallButton phone={c.phone} contactId={c.id} size="xs" />
                    </div>
                  ) : "—"}
                </td>
                <td className="py-2.5 px-3 text-slate-700 text-xs">
                  {c.country_code ? (
                    <span>{flagFromCode(c.country_code)} {countryLabel(c.country, c.country_code, lang)}</span>
                  ) : (c.country || "—")}
                </td>
                <td className="py-2.5 px-3">
                  <StatusBadge status={c.pipeline_status || "new"} />
                  {c.next_action_at && (
                    <div className="text-[10px] text-slate-500 mt-0.5">
                      {new Date(c.next_action_at).toLocaleString(L ? "el-GR" : "en-GB", { day: "2-digit", month: "short", hour: "2-digit", minute: "2-digit" })}
                    </div>
                  )}
                </td>
                <td className="py-2.5 px-3 whitespace-nowrap">
                  <button data-testid={`contact-timeline-${c.id}`} onClick={() => setTimelineFor(c)} className="p-1.5 text-slate-500 hover:text-[#0052CC]" title={L ? "Ιστορικό" : "History"}><History className="w-4 h-4" /></button>
                  <button data-testid={`contact-edit-${c.id}`} onClick={() => openEdit(c)} className="p-1.5 text-slate-500 hover:text-[#0052CC]"><Pencil className="w-4 h-4" /></button>
                  <button data-testid={`contact-delete-${c.id}`} onClick={() => del(c.id)} className="p-1.5 text-slate-500 hover:text-red-600"><Trash2 className="w-4 h-4" /></button>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      {/* Create/Edit modal */}
      {open && (
        <div className="fixed inset-0 bg-slate-900/40 z-50 flex items-center justify-center p-4" onClick={() => setOpen(false)}>
          <div className="bg-white rounded-lg max-w-2xl w-full max-h-[88vh] overflow-y-auto" onClick={(e) => e.stopPropagation()}>
            <div className="px-6 py-4 border-b border-slate-200 flex items-center justify-between">
              <h3 className="font-heading font-semibold text-slate-900">
                {editing ? (L ? "Επεξεργασία επαφής" : "Edit contact") : (L ? "Νέα επαφή" : "New contact")}
              </h3>
              <button onClick={() => setOpen(false)}><X className="w-4 h-4" /></button>
            </div>
            <form onSubmit={submit} className="px-6 py-4 space-y-3" data-testid="contact-form">
              <Field label={L ? "Επωνυμία εταιρείας" : "Company name"} testid="contact-company_name" value={form.company_name} onChange={(v) => setForm({ ...form, company_name: v })} required />
              <div className="grid grid-cols-2 gap-3">
                <Field label={L ? "Όνομα επαφής" : "First name"} testid="contact-first_name" value={form.first_name} onChange={(v) => setForm({ ...form, first_name: v })} required />
                <Field label={L ? "Επώνυμο επαφής" : "Last name"} testid="contact-last_name" value={form.last_name} onChange={(v) => setForm({ ...form, last_name: v })} />
              </div>
              <Field label={L ? "Θέση" : "Position"} testid="contact-position" value={form.position} onChange={(v) => setForm({ ...form, position: v })} />
              <Field label="Email" type="email" testid="contact-email" value={form.email} onChange={(v) => setForm({ ...form, email: v })} />
              <div>
                <label className="text-xs uppercase tracking-[0.2em] font-semibold text-slate-700">{L ? "Χώρα" : "Country"}</label>
                <CountrySelect testid="contact-country-select" value={form.country_code} onChange={(code) => setForm({ ...form, country_code: code, country: code })} />
              </div>
              <div>
                <label className="text-xs uppercase tracking-[0.2em] font-semibold text-slate-700">{L ? "Τηλέφωνο" : "Phone"}</label>
                <PhoneWithCountry
                  testid="contact-phone"
                  countryCode={form.country_code}
                  value={form.phone}
                  onChange={(v) => setForm({ ...form, phone: v })}
                />
              </div>
              <div className="grid grid-cols-2 gap-3">
                <Field label={L ? "Κλάδος" : "Industry"} testid="contact-industry" value={form.industry} onChange={(v) => setForm({ ...form, industry: v })} />
                <Field label="Website" testid="contact-website" value={form.website} onChange={(v) => setForm({ ...form, website: v })} />
              </div>
              <Field label="LinkedIn" testid="contact-linkedin" value={form.linkedin} onChange={(v) => setForm({ ...form, linkedin: v })} />
              <div>
                <label className="text-xs uppercase tracking-[0.2em] font-semibold text-slate-700">{L ? "Σημειώσεις" : "Notes"}</label>
                <textarea data-testid="contact-notes" value={form.notes || ""} onChange={(e) => setForm({ ...form, notes: e.target.value })} rows={3} className="mt-1.5 w-full px-3 py-2 border border-slate-200 rounded-md text-sm" />
              </div>
              <label className="flex items-center gap-2 cursor-pointer">
                <input type="checkbox" data-testid="contact-in-calling-list" checked={!!form.in_calling_list} onChange={(e) => setForm({ ...form, in_calling_list: e.target.checked })} />
                <span className="text-sm text-slate-700">
                  <ListPlus className="inline w-4 h-4 mr-1 -mt-0.5 text-emerald-600" />
                  {L ? "Προσθήκη στη λίστα κλήσεων" : "Add to calling list"}
                </span>
              </label>
              <div className="flex justify-end gap-2 pt-3 border-t border-slate-100">
                <button type="button" onClick={() => setOpen(false)} className="px-4 py-2 border border-slate-200 rounded-md text-sm font-medium">{t("common.cancel")}</button>
                <button type="submit" data-testid="contact-submit" className="px-4 py-2 bg-[#0052CC] text-white rounded-md text-sm font-semibold">
                  {editing ? (L ? "Αποθήκευση" : "Save") : t("common.create")}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

      {/* Timeline modal */}
      {timelineFor && (
        <ContactTimeline contactId={timelineFor.id} onClose={() => { setTimelineFor(null); load(); }} />
      )}
    </div>
  );
}

function Field({ label, testid, value, onChange, type = "text", required }) {
  return (
    <div>
      <label className="text-xs uppercase tracking-[0.2em] font-semibold text-slate-700">{label}</label>
      <input data-testid={testid} type={type} required={required} value={value || ""} onChange={(e) => onChange(e.target.value)} className="mt-1.5 w-full px-3 py-2 border border-slate-200 rounded-md text-sm" />
    </div>
  );
}
